import { slugifyBlogSlug } from "./blog";
import {
  getAwardShowLandingByName,
  getAwardShowLandingBySlug,
} from "./award-show-landings";

export const WINNERS_INDEX_PATH = "/winners";

const LIVE_RESULTS_WINDOW_MS = 48 * 60 * 60 * 1000;
const WINNERS_PARTY_PROMPT_LEAD_MS = 60 * 60 * 1000;

export type PublicWinnerNominee = {
  id: string;
  name: string;
  additionalInfo: string | null;
  imageUrl: string | null;
};

export type PublicWinnerCategory = {
  id: string;
  name: string;
  fieldName: string | null;
  orderIndex: number;
  nominees: PublicWinnerNominee[];
  winners: PublicWinnerNominee[];
  announcedAt: string | null;
};

export type WinnersShowRef = {
  id: string;
  name: string;
  displayName: string;
  year: number;
  slug: string;
  landingSlug: string | null;
  startTime: string | null;
  timezone: string | null;
  latestAnnouncedAt?: string | null;
};

/** Matches the award_shows.timezone column default. */
export const WINNERS_CEREMONY_TIMEZONE_FALLBACK = "America/Los_Angeles";

export type WinnersCeremonyStampPart = "date" | "time";

export function formatWinnersCeremonyStamp(
  iso: string | null | undefined,
  locale: string,
  timezone: string | null | undefined,
  part: WinnersCeremonyStampPart,
): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const zone = timezone?.trim() || WINNERS_CEREMONY_TIMEZONE_FALLBACK;
  const options: Intl.DateTimeFormatOptions =
    part === "time"
      ? {
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
          timeZone: zone,
        }
      : {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: zone,
        };

  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    const fallback = { ...options };
    delete fallback.timeZone;
    return new Intl.DateTimeFormat(locale, fallback).format(date);
  }
}

export type PublicWinnersShow = WinnersShowRef & {
  officialName: string;
  titleSingular: string;
  imageUrl: string | null;
  latestAnnouncedAt: string | null;
  categories: PublicWinnerCategory[];
};

export type WinnersSlugSource = {
  id: string;
  name: string;
  year: number;
  createdAt?: string | null;
};

export type WinnersShowNames = {
  displayName: string;
  officialName: string;
  landingSlug: string | null;
};

export type WinnersPromotionKind = "current-show" | "next-show" | "franchise";

export type WinnersPromotion = {
  kind: WinnersPromotionKind;
  showId: string | null;
  displayName: string;
  year: number;
  winnersPath: string | null;
  landingPath: string | null;
  createPartyPath: string;
};

export type WinnersCategoryGroup = {
  fieldName: string | null;
  categories: PublicWinnerCategory[];
};

export type WinnersStatus = "upcoming" | "live" | "complete";

export type WinnersProgress = {
  total: number;
  announced: number;
  remaining: number;
  nominees: number;
  percent: number;
  status: WinnersStatus;
};

export function getWinnersProgress(
  categories: ReadonlyArray<Pick<PublicWinnerCategory, "nominees" | "winners">>,
): WinnersProgress {
  const total = categories.length;
  const announced = categories.filter(
    (category) => category.winners.length > 0,
  ).length;
  const nominees = categories.reduce(
    (sum, category) => sum + category.nominees.length,
    0,
  );
  const status: WinnersStatus =
    total > 0 && announced >= total
      ? "complete"
      : announced > 0
        ? "live"
        : "upcoming";
  return {
    total,
    announced,
    remaining: total - announced,
    nominees,
    percent: total === 0 ? 0 : Math.round((announced / total) * 100),
    status,
  };
}

export function shouldShowWinnersPartyPrompt(input: {
  startTime: string | null;
  announcedCount: number;
  now?: Date;
}): boolean {
  if (input.announcedCount > 0) {
    return false;
  }
  const startMs = timestamp(input.startTime);
  const nowMs = (input.now ?? new Date()).getTime();
  if (startMs === null || Number.isNaN(nowMs)) {
    return false;
  }
  return startMs - nowMs >= WINNERS_PARTY_PROMPT_LEAD_MS;
}

export function winnersShowPath(slug: string): string {
  return `${WINNERS_INDEX_PATH}/${slug}`;
}

export function winnersCreatePartyPath(showId: string | null): string {
  if (!showId) {
    return "/parties/create";
  }
  return `/parties/create?showId=${encodeURIComponent(showId)}`;
}

export function buildWinnersSlug(name: string, year: number): string {
  const landing = getAwardShowLandingByName(name);
  const base = landing?.slug || slugifyBlogSlug(name.replace(/\b\d{4}\b/g, " ")) || "show";
  const suffix = String(year);
  if (base.endsWith(`-${suffix}`)) {
    return base;
  }
  return `${base}-${suffix}`;
}

export function assignWinnersSlugs(
  shows: readonly WinnersSlugSource[],
): Map<string, string> {
  const ordered = [...shows].sort((left, right) => {
    const leftTime = timestamp(left.createdAt) ?? 0;
    const rightTime = timestamp(right.createdAt) ?? 0;
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    return left.id.localeCompare(right.id);
  });

  const used = new Set<string>();
  const assigned = new Map<string, string>();

  for (const show of ordered) {
    const slug = uniqueSlug(buildWinnersSlug(show.name, show.year), show.id, used);
    used.add(slug);
    assigned.set(show.id, slug);
  }

  return assigned;
}

export function getWinnersShowNames(name: string): WinnersShowNames {
  const landing = getAwardShowLandingByName(name);
  if (!landing) {
    const trimmed = name.trim() || "Award show";
    return {
      displayName: trimmed,
      officialName: trimmed,
      landingSlug: null,
    };
  }

  return {
    displayName: landing.shortName,
    officialName: landing.officialName,
    landingSlug: landing.slug,
  };
}

export function getWinnersIndexKeywords(): string[] {
  return [
    "award show winners",
    "award show winners list",
    "oscar winners list",
    "emmy winners list",
    "grammy winners",
    "golden globe winners",
    "live award show results",
    "who won the oscars",
  ];
}

export function getWinnersSearchKeywords(input: {
  displayName: string;
  officialName: string;
  titleSingular: string;
  year: number;
}): string[] {
  const { displayName, officialName, titleSingular, year } = input;
  const candidates = [
    `${year} ${displayName} winners`,
    `${year} ${displayName} winners list`,
    `${displayName} winners ${year}`,
    `${officialName} ${year} winners`,
    `${titleSingular} winners ${year}`,
    `who won the ${year} ${displayName}`,
    `${displayName} winners live`,
    `complete ${displayName} winners list`,
    `${year} ${displayName} results`,
  ];
  const seen = new Set<string>();
  const keywords: string[] = [];

  for (const keyword of candidates) {
    const key = keyword.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    keywords.push(keyword);
  }

  return keywords;
}

export function clipSearchDescription(text: string, max = 160): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= max) {
    return normalized;
  }

  const budget = max - 3;
  const sliced = normalized.slice(0, budget);
  const lastSpace = sliced.lastIndexOf(" ");
  const clipped = (lastSpace > 40 ? sliced.slice(0, lastSpace) : sliced).trimEnd();
  return `${clipped}...`;
}

export function pickWinnersPromotion(
  current: WinnersShowRef,
  catalog: readonly WinnersShowRef[],
  now: Date = new Date(),
): WinnersPromotion {
  const nowMs = now.getTime();
  const upcoming = catalog
    .filter((show) => {
      const start = timestamp(show.startTime);
      return start !== null && start > nowMs;
    })
    .sort(
      (left, right) =>
        (timestamp(left.startTime) ?? 0) - (timestamp(right.startTime) ?? 0),
    );

  const next = upcoming[0];
  if (next && next.id === current.id) {
    return toPromotion("current-show", current, false, null);
  }
  if (next) {
    return toPromotion("next-show", next, true, null);
  }
  return toPromotion("franchise", current, false, "/shows");
}

/**
 * While a ceremony is in progress, announced categories lead in the order
 * they were called. Pending categories stay in ballot order under their
 * fields. A fully announced or still-unannounced show keeps field groups.
 */
export function groupWinnersCategories(
  categories: readonly PublicWinnerCategory[],
): WinnersCategoryGroup[] {
  const announced = categories.filter((category) => category.winners.length > 0);
  const pending = categories.filter((category) => category.winners.length === 0);
  if (announced.length === 0 || pending.length === 0) {
    return groupWinnersCategoriesByField(
      [...categories].sort(compareWinnersBallotOrder),
    );
  }

  return [
    ...groupConsecutiveWinnersCategories(
      [...announced].sort(comparePublicWinnerCategories),
    ),
    ...groupWinnersCategoriesByField([...pending].sort(compareWinnersBallotOrder)),
  ];
}

/** Announced categories first, earliest call first, then ballot order. */
export function comparePublicWinnerCategories(
  left: PublicWinnerCategory,
  right: PublicWinnerCategory,
): number {
  const leftAnnounced = left.winners.length > 0;
  const rightAnnounced = right.winners.length > 0;
  if (leftAnnounced !== rightAnnounced) {
    return leftAnnounced ? -1 : 1;
  }
  if (leftAnnounced) {
    const leftTime = timestamp(left.announcedAt);
    const rightTime = timestamp(right.announcedAt);
    if (leftTime !== rightTime) {
      if (leftTime === null) {
        return 1;
      }
      if (rightTime === null) {
        return -1;
      }
      return leftTime - rightTime;
    }
  }
  return compareWinnersBallotOrder(left, right);
}

function compareWinnersBallotOrder(
  left: Pick<PublicWinnerCategory, "orderIndex" | "name">,
  right: Pick<PublicWinnerCategory, "orderIndex" | "name">,
): number {
  return (
    left.orderIndex - right.orderIndex || left.name.localeCompare(right.name)
  );
}

function winnersFieldKey(fieldName: string | null): string {
  return fieldName?.trim() || "";
}

function groupWinnersCategoriesByField(
  ordered: readonly PublicWinnerCategory[],
): WinnersCategoryGroup[] {
  const groupOrder: string[] = [];
  const groups = new Map<string, PublicWinnerCategory[]>();

  for (const category of ordered) {
    const fieldName = winnersFieldKey(category.fieldName);
    if (!groups.has(fieldName)) {
      groupOrder.push(fieldName);
      groups.set(fieldName, []);
    }
    groups.get(fieldName)?.push(category);
  }

  return groupOrder.map((fieldName) => ({
    fieldName: fieldName || null,
    categories: groups.get(fieldName) ?? [],
  }));
}

function groupConsecutiveWinnersCategories(
  ordered: readonly PublicWinnerCategory[],
): WinnersCategoryGroup[] {
  const groups: WinnersCategoryGroup[] = [];

  for (const category of ordered) {
    const fieldName = winnersFieldKey(category.fieldName) || null;
    const last = groups.at(-1);
    if (last && last.fieldName === fieldName) {
      last.categories.push(category);
      continue;
    }
    groups.push({ fieldName, categories: [category] });
  }

  return groups;
}

/**
 * Winners stay first. Everyone else follows the public crowd share when a
 * category has been published, and otherwise stays in ballot order.
 * A winner missing from the nominee list is still included.
 */
export function listWinnersPageNominees<T extends { id: string }>(
  nominees: readonly T[],
  winners: readonly T[],
  shares?: Readonly<Record<string, { sharePercent: number }>> | null,
): Array<{ nominee: T; isWinner: boolean }> {
  const byId = new Map(nominees.map((nominee) => [nominee.id, nominee]));
  const seen = new Set<string>();
  const listedWinners: T[] = [];

  for (const winner of winners) {
    if (seen.has(winner.id)) {
      continue;
    }
    seen.add(winner.id);
    listedWinners.push(byId.get(winner.id) ?? winner);
  }

  const rest = nominees.filter((nominee) => !seen.has(nominee.id));
  const ranked = shares ? rankNomineesByShare(rest, shares) : rest;

  return [
    ...listedWinners.map((nominee) => ({ nominee, isWinner: true })),
    ...ranked.map((nominee) => ({ nominee, isWinner: false })),
  ];
}

function rankNomineesByShare<T extends { id: string }>(
  nominees: readonly T[],
  shares: Readonly<Record<string, { sharePercent: number }>>,
): T[] {
  return nominees
    .map((nominee, index) => ({
      nominee,
      index,
      share: shares[nominee.id]?.sharePercent ?? -1,
    }))
    .sort(
      (left, right) => right.share - left.share || left.index - right.index,
    )
    .map((entry) => entry.nominee);
}

export function orderNomineesWithWinnersFirst<T extends { id: string }>(
  nominees: readonly T[],
  winnerIds: readonly string[],
): T[] {
  const byId = new Map(nominees.map((nominee) => [nominee.id, nominee]));
  const winnerSet = new Set(winnerIds);
  const winners: T[] = [];

  for (const winnerId of winnerIds) {
    const nominee = byId.get(winnerId);
    if (nominee) {
      winners.push(nominee);
    }
  }

  const rest = nominees.filter((nominee) => !winnerSet.has(nominee.id));
  return [...winners, ...rest];
}

export function compareWinnersIndexOrder(
  left: WinnersShowRef,
  right: WinnersShowRef,
  now: Date = new Date(),
): number {
  const nowMs = now.getTime();
  const leftLive = isFreshResult(left.latestAnnouncedAt, nowMs);
  const rightLive = isFreshResult(right.latestAnnouncedAt, nowMs);
  if (leftLive !== rightLive) {
    return leftLive ? -1 : 1;
  }
  if (leftLive && rightLive) {
    const announcedDiff =
      (timestamp(right.latestAnnouncedAt) ?? 0) -
      (timestamp(left.latestAnnouncedAt) ?? 0);
    if (announcedDiff !== 0) {
      return announcedDiff;
    }
  }

  const leftStart = timestamp(left.startTime);
  const rightStart = timestamp(right.startTime);
  const leftUpcoming = leftStart !== null && leftStart > nowMs;
  const rightUpcoming = rightStart !== null && rightStart > nowMs;
  if (leftUpcoming !== rightUpcoming) {
    return leftUpcoming ? -1 : 1;
  }
  if (leftUpcoming && rightUpcoming && leftStart !== rightStart) {
    return (leftStart ?? 0) - (rightStart ?? 0);
  }

  const announcedDiff =
    (timestamp(right.latestAnnouncedAt) ?? 0) -
    (timestamp(left.latestAnnouncedAt) ?? 0);
  if (announcedDiff !== 0) {
    return announcedDiff;
  }
  return (rightStart ?? 0) - (leftStart ?? 0);
}

export function getRelatedWinnersShows<T extends WinnersShowRef>(
  current: T,
  catalog: readonly T[],
  limit = 6,
): T[] {
  const currentCategory = landingCategory(current.landingSlug);
  const others = catalog.filter((show) => show.id !== current.id);
  const sameCategory = others.filter(
    (show) =>
      currentCategory !== null && landingCategory(show.landingSlug) === currentCategory,
  );
  const sameIds = new Set(sameCategory.map((show) => show.id));
  const rest = others.filter((show) => !sameIds.has(show.id));
  return [...sameCategory, ...rest].slice(0, limit);
}

function landingCategory(slug: string | null): string | null {
  if (!slug) {
    return null;
  }
  return getAwardShowLandingBySlug(slug)?.category ?? null;
}

function toPromotion(
  kind: WinnersPromotionKind,
  show: WinnersShowRef,
  includeWinnersPath: boolean,
  landingFallback: string | null,
): WinnersPromotion {
  return {
    kind,
    showId: kind === "franchise" ? null : show.id,
    displayName: show.displayName,
    year: show.year,
    winnersPath: includeWinnersPath ? winnersShowPath(show.slug) : null,
    landingPath: show.landingSlug ? `/${show.landingSlug}` : landingFallback,
    createPartyPath: winnersCreatePartyPath(kind === "franchise" ? null : show.id),
  };
}

function uniqueSlug(base: string, id: string, used: Set<string>): string {
  if (!used.has(base)) {
    return base;
  }
  const compact = id.replace(/-/g, "");
  const shortened = `${base}-${compact.slice(0, 8)}`;
  if (!used.has(shortened)) {
    return shortened;
  }
  return `${base}-${compact}`;
}

function isFreshResult(
  announcedAt: string | null | undefined,
  nowMs: number,
): boolean {
  const announced = timestamp(announcedAt);
  if (announced === null || announced > nowMs) {
    return false;
  }
  return nowMs - announced < LIVE_RESULTS_WINDOW_MS;
}

function timestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}
